import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { RouteContextType } from "../../hooks/use-route-context/UseRouteContext";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import NavigationDataService from "../../services/navigation-data-service/NavigationDataService";


export interface IBreadCrumbData {
    breadcrumbItems: INavigationItem[];
    relatedItems: INavigationItem[];
    validGroupViews: IKeyValuePair<number, UIViewEnum[]>[];
    contextObjectName: string;
}


const BreadCrumbUtils = {

    getData: async function (customerID: number, viewID: UIViewEnum | undefined,
        routeContext: RouteContextType,
        offline: boolean,
        offlineDataLoaded: boolean,
        signal?: AbortSignal): Promise<IBreadCrumbData> {

        const returnData: IBreadCrumbData = {
            breadcrumbItems: [],
            relatedItems: [],
            validGroupViews: [],
            contextObjectName: ''
        };

        if (!routeContext.contextObject || viewID === undefined) {
            return returnData;
        }

        let group: INavigationItem | undefined;
        let relatedObjects: INavigationItem[] = [];

        if (!offline && !offlineDataLoaded) {

            //console.log(`loading from api ${routeContext.groupID}`);

            const p1 = NavigationDataService.loadNavigationGroups(signal);
            const p2 = NavigationDataService.getRelatedObjects(
                customerID,
                routeContext.groupID as number,
                routeContext.contextObject as IeLynxObject,
                viewID, {
                tankBatteryID: routeContext.tankBatteryID,
                wellID: routeContext.wellID,
                deviceID: routeContext.deviceID,
                tankID: routeContext.tankID
            },
                offline,
                signal);

            const [groups, b] = await Promise.all([p1, p2]);

            group = groups.find(o => o.objectID === routeContext.groupID);

            if (group) {
                const children = groups.filter(o => o.parentID === group?.objectID)
                if (children) {
                    group.children = children as INavigationItem[];
                }

                const ancestors: INavigationItem[] = [];

                for (let parentID = group?.parentID; parentID;) {
                    const parent = groups?.find(o => o.objectID === parentID) as INavigationItem;
                    if (parent.hasChildren) {
                        parent.children = groups.filter(o => o.parentID === parent.objectID);
                    }
                    ancestors.unshift(parent);
                    parentID = parent.parentID;
                }

                group.ancestors = ancestors as INavigationItem[];
                group.fullPath = group.ancestors.map(o => o.title).join('/') + `/${group.title}`;
            }

            relatedObjects = b;

        } else {
            //console.log(`loading from offline ${routeContext.groupID}`);

            const p1 = NavigationDataService.getNavigationGroup(
                customerID,
                routeContext.groupID as number, {
                includeChildren: true,
                includeAncestors: true
            });

            const p2 = NavigationDataService.getRelatedObjects(
                customerID,
                routeContext.groupID as number,
                routeContext.contextObject as IeLynxObject,
                viewID, {
                tankBatteryID: routeContext.tankBatteryID,
                wellID: routeContext.wellID,
                deviceID: routeContext.deviceID,
                tankID: routeContext.tankID
            },
                offline,
                signal);

            const [a, b] = await Promise.all([p1, p2]);

            group = a;
            relatedObjects = b;
        }

        if (group) {

            let items = group?.ancestors ?? [];

            items.push(group as INavigationItem);

            const groupViews = await NavigationDataService.getAvailableViewsForGroups(customerID,
                routeContext.groupID as number,
                items.map((o) => o.objectID),
                offline,
                signal);

            if (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Device) {
                const parent = relatedObjects.find(o => o.objectTypeID === eLynxObjectTypeEnum.Well);
                if (parent) {
                    items.push(parent);
                }
            } else if (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tank) {
                const parent = relatedObjects.find(o => o.objectTypeID === eLynxObjectTypeEnum.Device || o.objectTypeID === eLynxObjectTypeEnum.TankBattery);
                if (parent) {
                    items.push(parent);
                }
            }
            else if (routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tag) {
                items = items.concat(relatedObjects.filter(o => o.objectTypeID !== eLynxObjectTypeEnum.Tag));
                relatedObjects = relatedObjects.filter(o => o.objectTypeID === eLynxObjectTypeEnum.Tag);
            }

            const co = relatedObjects.find(o => routeContext.contextObject?.objectType && o.objectID === routeContext.contextObject?.id);
            if (co) {
                items.push(co);
            }

            returnData.breadcrumbItems = items;
            //returnData.relatedItems = relatedObjects.filter(o => o.objectTypeID === routeContext?.contextObject?.objectType && o.objectID !== routeContext?.contextObject.id);
            returnData.relatedItems = relatedObjects.filter(o => o.objectTypeID === routeContext?.contextObject?.objectType);
            returnData.validGroupViews = groupViews;

            if (routeContext.contextObject) {
                if (routeContext.contextObject.objectType === eLynxObjectTypeEnum.Group) {
                    returnData.contextObjectName = group.title;
                } else {
                    returnData.contextObjectName = relatedObjects.find(o => o.objectTypeID === routeContext?.contextObject?.objectType &&
                        o.objectID === routeContext?.contextObject.id)?.title ?? '';
                }
            }
        }

        return returnData;
    }
};

export default BreadCrumbUtils;
